import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

/** One field of the form, as the review panel needs it. */
export interface CuraFillField {
  fieldId: string;
  itemRef: string | null;
  label: string;
  acroName: string | null;
  /** The name this field carries INSIDE the PDF — what `formData` is keyed by. */
  pdfFieldName: string | null;
  kind: 'text' | 'multiline' | 'comb' | 'checkbox' | 'radio' | 'choice' | 'signature' | string;
  page: number;
  rect: number[] | null;
  options: string[] | null;
  cells: number | null;
  maxLen: number | null;
  required: boolean;
  value: string | null;
  /** The value as the page carries it (dates in the boxes' order, +27 collapsed). */
  pdfValue?: string | null;
  source: { kind: string; ref: string; quote?: string | null } | null;
  confidence: number | null;
  reason?: string | null;
  message?: string | null;
  findings: { code: string; severity: string; detail: string }[];
  /** `prefilled` = the form arrived with this value; neither CURA's work nor a blank. */
  disposition: 'filled' | 'blank' | 'prefilled';
}

export interface CuraFillNeedsYou {
  fieldId: string;
  itemRef: string | null;
  label: string | null;
  disposition: 'blank' | 'filled_needs_check';
  reason: string;
  message: string;
}

export interface CuraFormFill {
  id: string;
  status: 'queued' | 'detecting' | 'planning' | 'rendering' | 'verifying'
  | 'awaiting_review' | 'accepted' | 'failed';
  lane: string | null;
  progress: number;
  message: string | null;
  errorMessage: string | null;
  fileName: string | null;
  createdAt?: string | null;
  verdict: 'pass' | 'pass-with-warnings' | 'block';
  counts: { filled: number; blank: number; needsCheck: number; total: number } | null;
  prose: string | null;
  needsYou: CuraFillNeedsYou[];
  fields: CuraFillField[];
  /** There is a document to look at. Not the same as being allowed to download it. */
  canPreview: boolean;
  canDownloadEditable: boolean;
  canDownloadSigned: boolean;
  /** Already added to the patient timeline. */
  promoted: boolean;
}

export interface CuraFillProgress {
  fillId: string;
  status: string;
  message: string;
  progress: number | null;
  completed: boolean;
  failed: boolean;
  prose?: string;
  counts?: { filled: number; blank: number; needsCheck: number; total: number };
  verdict?: string;
  fileName?: string;
  refused?: boolean;
  reason?: string;
}

/** What the server says about whether a value fits its box. */
export interface CuraFitResult {
  fieldId: string;
  fits: boolean;
  truncated: boolean;
  fontSize: number | null;
  wouldRender?: string;
  droppedCharacters?: string[] | null;
}

/**
 * Client for CURA form fills.
 *
 * NOTE there is no method that downloads an un-accepted fill: the server refuses it, because
 * review is a state transition rather than a flag, and offering one here would only produce a
 * button that always fails.
 */
@Injectable({ providedIn: 'root' })
export class CuraFormFillService {
  private apiUrl = 'api';

  constructor(private http: HttpClient) { }

  /** The socket event a running fill reports on. The dialog is already in this room. */
  progressEvent(conversationKey: string): string {
    return `curaFormFill-${conversationKey}-progress`;
  }

  get(id: string): Observable<CuraFormFill> {
    return this.http
      .get<{ success: boolean; data: CuraFormFill }>(`${this.apiUrl}/cura-form-fills/${id}`)
      .pipe(map(r => r.data));
  }

  list(patientId: string, conversationKey: string): Observable<CuraFormFill[]> {
    return this.http
      .get<{ success: boolean; data: CuraFormFill[] }>(
        `${this.apiUrl}/cura-form-fills?patientId=${patientId}&conversationKey=${conversationKey}`)
      .pipe(map(r => r.data));
  }

  /**
   * Finalise a fill.
   *
   * `edits` must contain ONLY the fields the operator actually changed. Every value posted here
   * is re-sourced as `user-supplied`, so posting the whole set would erase the provenance on
   * every untouched field and make the next review meaningless.
   */
  accept(id: string, edits: { fieldId: string; value: string }[] = [], sign = false)
    : Observable<CuraFormFill> {
    return this.http
      .post<{ success: boolean; data: CuraFormFill }>(
        `${this.apiUrl}/cura-form-fills/${id}/accept`, { edits, sign })
      .pipe(map(r => r.data));
  }

  /**
   * "Will these values fit their boxes?"
   *
   * The viewer previews an edit instantly, but it lays text out its own way — it will show a
   * value the renderer would shorten. This asks the code that will actually do the rendering,
   * so the panel's warning and the final PDF cannot disagree.
   */
  checkFit(id: string, values: { fieldId: string; value: string }[])
    : Observable<CuraFitResult[]> {
    return this.http
      .post<{ success: boolean; data: { results: CuraFitResult[] } }>(
        `${this.apiUrl}/cura-form-fills/${id}/fit`, { values })
      .pipe(map(r => r.data.results));
  }

  promote(id: string, body: { title?: string; date?: string } = {})
    : Observable<{ noteId: string; mediaId: string; fill: CuraFormFill }> {
    return this.http
      .post<{ success: boolean; data: { noteId: string; mediaId: string; fill: CuraFormFill } }>(
        `${this.apiUrl}/cura-form-fills/${id}/promote`, body)
      .pipe(map(r => r.data));
  }

  /**
   * The document, for looking at while reviewing it.
   *
   * Separate from `downloadUrl` because that one refuses anything unaccepted — which is right
   * for a finished artefact and wrong for the pane the doctor is editing against.
   */
  previewUrl(id: string): Observable<{ url: string; variant: string; lane: string | null }> {
    return this.http
      .get<{ success: boolean; data: { url: string; variant: string; lane: string | null } }>(
        `${this.apiUrl}/cura-form-fills/${id}/preview`)
      .pipe(map(r => r.data));
  }

  downloadUrl(id: string, variant: 'editable' | 'signed' = 'editable')
    : Observable<{ url: string; fileName: string; variant: string }> {
    return this.http
      .get<{ success: boolean; data: { url: string; fileName: string; variant: string } }>(
        `${this.apiUrl}/cura-form-fills/${id}/download?variant=${variant}`)
      .pipe(map(r => r.data));
  }

  /** Human wording for a gap reason, so the panel does not show enum constants. */
  reasonLabel(reason: string): string {
    switch (reason) {
      case 'NOT_IN_RECORD': return 'Not in the patient file';
      case 'NOT_APPLICABLE': return "Doesn't apply to this patient";
      case 'AMBIGUOUS': return 'Conflicting information';
      case 'NEEDS_CLINICAL_JUDGEMENT': return 'Your clinical judgement';
      case 'NEEDS_PATIENT_INPUT': return 'For the patient';
      case 'LOW_CONFIDENCE': return 'Not certain';
      case 'FIELD_UNCERTAIN': return 'Unsure what this field asks';
      case 'DRAFTED': return 'Drafted — please check';
      case 'ROW_UNCERTAIN': return 'Check it belongs to this row';
      case 'NOT_OURS': return 'For another party';
      case 'UNGROUNDED': return 'Could not be traced to the record';
      case 'LABEL_MISMATCH': return 'May be in the wrong field';
      case 'VALUE_CONTRADICTS_RECORD': return 'Contradicts the record';
      case 'MISSED_FIELD': return 'May have been missed';
      case 'OVERFLOW': return 'Too long for the space';
      case 'DOES_NOT_FIT': return 'Too long for the boxes';
      case 'REQUIRED_BLANK': return 'The form requires this';
      case 'FORMAT_INVALID': return 'Does not look right for this field';
      default: return reason;
    }
  }

  /** Where a value came from, in words a doctor would use. */
  sourceLabel(source: CuraFillField['source']): string {
    if (!source) return '';
    switch (source.kind) {
      case 'patient-field': return 'From the patient record';
      case 'note': return 'From a clinical note';
      case 'icd': return 'From a coded diagnosis';
      case 'procedure-code': return 'From a billed procedure';
      case 'derived': return 'Worked out from the record';
      case 'user-supplied': return 'You entered this';
      default: return source.kind;
    }
  }

  /**
   * A control that cannot produce an invalid value never needs the server asked (task R3.3).
   *
   * A select cannot emit an off-list option, a checkbox cannot be malformed, and a comb input
   * capped at its cell count cannot overflow. Only free text can.
   */
  needsFitCheck(field: CuraFillField): boolean {
    return field.kind === 'text' || field.kind === 'multiline';
  }
}
